import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { isDevBypassAuth } from 'src/api/state';
// TEMPORARY: the reports/documents domain is swapped to the real backend, but
// the endpoints aren't on prod yet — this serves fixture data under dev-bypass
// so the Reports UI stays clickable meanwhile. Delete with src/api/reports/
// dev-mock.ts once the reports backend is live on prod.
import { reportsDevMatchers } from 'src/api/reports/dev-mock';
// Dev-only mock layer for the citizen dev-bypass (DEV_BYPASS_AUTH=true on the
// dev pill or .env): serves a generic 3-property mock to skip the MitID gate
// on local dev.

type PropertyFixture = {
  id: string;
  location: {
    latitude: number;
    longitude: number;
    name: string;
  };
};

const MOCK_PROPERTIES: PropertyFixture[] = [
  {
    id: 'mock-prop-1',
    location: {
      latitude: 56.1629,
      longitude: 10.2039,
      name: 'Hovedgaden 1, 8000 Aarhus',
    },
  },
  {
    id: 'mock-prop-2',
    location: {
      latitude: 55.6761,
      longitude: 12.5683,
      name: 'Strandvejen 42, 2100 København',
    },
  },
  {
    id: 'mock-prop-3',
    location: {
      latitude: 55.4038,
      longitude: 10.4024,
      name: 'Kongevej 7, 5000 Odense',
    },
  },
];

const makeTicketType = (
  id: string,
  name: string,
  serviceType: string,
  isContainerLevel = false
) => ({
  id,
  name,
  service: { id: `svc-${id}`, name: serviceType, service_type: serviceType },
  is_container_level: isContainerLevel,
  is_property_level: !isContainerLevel,
  portal_require_contact_information: false,
  portal_description: `Mock description for ${name}`,
  portal_date_label: 'Preferred date',
  portal_date_help_text: '',
  portal_date_required: false,
  portal_date_hidden: false,
  portal_note_label: 'Note',
  portal_note_help_text: '',
  portal_note_required: false,
  portal_note_hidden: false,
  portal_price_show_zero: true,
  portal_contact_information_name_required: false,
  portal_contact_information_phone_number_required: false,
  portal_contact_information_email_required: false,
  portal_contact_information_next_button_label: 'Next',
  portal_confirm_title: 'Confirm',
  portal_confirm_subtitle: '',
  portal_service_type_bulk_waste_limit: 10,
  portal_service_type_bulk_waste_limit_label: 'Items',
  portal_service_type_bulk_waste_limit_help_text: '',
  portal_dropdown_hidden: true,
  portal_dropdown_required: false,
  portal_dropdown_label: '',
  portal_dropdown_help_text: '',
  portal_dropdown_options: [],
});

const buildMockProperty = (id: string) => {
  const listEntry =
    MOCK_PROPERTIES.find((p) => p.id === id) ?? MOCK_PROPERTIES[0];
  return {
    id,
    type: { id: 'type-1', name: 'Residential' },
    location: listEntry.location,
    ticket_types: [
      makeTicketType('tt-1', 'Extra emptying', 'extra_emptying', true),
      makeTicketType('tt-2', 'Bulk waste pickup', 'bulk_waste_pickup', false),
      makeTicketType('tt-3', 'Missing collection', 'missing_collection', true),
    ],
  };
};

type MockResult = { status: number; data: unknown };
type Matcher = (config: InternalAxiosRequestConfig) => MockResult | null;

const citizenMatchers: Matcher[] = [
  // Property search list: GET property OR /property, optional ?search=...
  (config) => {
    if (config.method?.toUpperCase() !== 'GET') return null;
    const url = (config.url || '').replace(/^\/+/, '');
    if (!/^property\/?(\?|$)/.test(url)) return null;
    return { status: 200, data: MOCK_PROPERTIES };
  },
  // Property detail: GET property/:id (leaf, not property/:id/sub)
  (config) => {
    if (config.method?.toUpperCase() !== 'GET') return null;
    const url = (config.url || '').replace(/^\/+/, '');
    const match = /^property\/([^/?]+)\/?(\?|$)/.exec(url);
    if (!match) return null;
    return { status: 200, data: buildMockProperty(match[1]) };
  },
];

const applyMock = (
  config: InternalAxiosRequestConfig,
  hit: MockResult,
  source: 'citizen' | 'reports'
) => {
  console.info(
    `[devMock:${source}] intercepted ${config.method?.toUpperCase()} ${config.url}`
  );

  config.adapter = async () => ({
    data: hit.data,
    status: hit.status,
    statusText: 'OK (dev mock)',
    headers: {},
    config,
  });
};

export const installDevMocks = (...instances: AxiosInstance[]) => {
  // Per-request adapter override via interceptor — leaves axios's default
  // adapter resolution intact for everything else. Install on every instance
  // the app uses since axios interceptors don't propagate across instances.
  // The bypass check re-evaluates per request so toggling the dev bypass
  // takes effect immediately.
  instances.forEach((instance) => {
    instance.interceptors.request.use(async (config) => {
      if (isDevBypassAuth()) {
        const hit = citizenMatchers
          .map((tryMatch) => tryMatch(config))
          .find(Boolean);
        if (hit) {
          applyMock(config, hit, 'citizen');
          return config;
        }
        // TEMPORARY reports/documents fixtures (see import note above).
        const reportsHit = reportsDevMatchers
          .map((tryMatch) => tryMatch(config))
          .find(Boolean);
        if (reportsHit) {
          applyMock(config, reportsHit, 'reports');
          return config;
        }
      }
      return config;
    });
  });
};
